class EcofoxMegaMenuController {
  constructor(details) {
    this.details = details;
    this.summary = details.querySelector('summary');
    this.panel = this.summary?.nextElementSibling;
    this.openTimer = null;
    this.closeTimer = null;
    this.animationTimer = null;
    this.animationDuration = 300;
    this.isOpening = false;
    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

    if (!this.summary || !this.panel) return;

    this.bindEvents();
  }

  bindEvents() {
    this.summary.addEventListener('click', (event) => this.onSummaryClick(event));
    this.details.addEventListener('mouseenter', () => this.open());
    this.details.addEventListener('mouseleave', () => this.scheduleClose());
    this.details.addEventListener('focusin', () => this.open());
    this.details.addEventListener('focusout', () => this.scheduleFocusClose());
    this.details.addEventListener('keydown', (event) => this.onKeyDown(event));
    this.details.addEventListener('toggle', () => this.syncState());
    window.addEventListener('resize', () => {
      if (this.details.hasAttribute('open')) this.setPanelPosition();
    });
  }

  open(delay = 30) {
    if (!this.canHover.matches) return;
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.openTimer);

    this.openTimer = window.setTimeout(() => this.show(), delay);
  }

  show() {
    window.clearTimeout(this.animationTimer);
    this.closeSiblingMenus();
    this.setPanelPosition();
    this.isOpening = true;
    this.details.setAttribute('open', '');
    this.details.classList.remove('is-closing');
    document.body.classList.add('ecofox-mega-menu-open');
    this.summary.setAttribute('aria-expanded', 'true');

    window.requestAnimationFrame(() => {
      this.details.classList.add('is-open');
      this.isOpening = false;
    });
  }

  onSummaryClick(event) {
    if (!this.canHover.matches) return;
    event.preventDefault();

    if (this.details.classList.contains('is-open')) {
      this.close();
      return;
    }

    this.open(0);
  }

  setPanelPosition() {
    const header = this.details.closest('.header') || document.querySelector('.ecofox-header, .header');
    const headerBottom = header ? Math.max(0, Math.floor(header.getBoundingClientRect().bottom) - 1) : 0;
    document.documentElement.style.setProperty('--ecofox-mega-menu-top', `${headerBottom}px`);
  }

  scheduleClose() {
    if (!this.canHover.matches) return;
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.close(), 160);
  }

  scheduleFocusClose() {
    window.setTimeout(() => {
      if (!this.details.contains(document.activeElement)) this.close();
    });
  }

  close() {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.animationTimer);

    if (!this.details.hasAttribute('open')) return;

    this.details.classList.remove('is-open');
    this.details.classList.add('is-closing');
    this.summary.setAttribute('aria-expanded', 'false');

    this.animationTimer = window.setTimeout(() => {
      this.details.classList.remove('is-closing');
      this.details.removeAttribute('open');
      this.unlockBodyIfAllClosed();
    }, this.animationDuration);
  }

  closeSiblingMenus() {
    this.details
      .closest('.header__inline-menu')
      ?.querySelectorAll('[data-ecofox-mega-menu][open]')
      .forEach((details) => {
        if (details !== this.details) {
          details.classList.remove('is-open', 'is-closing');
          details.removeAttribute('open');
          details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      });
  }

  syncState() {
    const isOpen = this.details.hasAttribute('open');
    const isAnimatingClosed = this.details.classList.contains('is-closing');

    if (this.isOpening) {
      this.summary.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ecofox-mega-menu-open');
      return;
    }
    this.details.classList.toggle('is-open', isOpen && !isAnimatingClosed);
    this.summary.setAttribute('aria-expanded', isOpen && !isAnimatingClosed ? 'true' : 'false');

    if (isOpen && !isAnimatingClosed && this.canHover.matches) {
      this.setPanelPosition();
      document.body.classList.add('ecofox-mega-menu-open');
    } else if (!isOpen) {
      this.unlockBodyIfAllClosed();
    }
  }

  unlockBodyIfAllClosed() {
    if (!document.querySelector('[data-ecofox-mega-menu][open]')) {
      document.body.classList.remove('ecofox-mega-menu-open');
    }
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;
    this.close();
    this.summary.focus();
  }
}

function initEcofoxMegaMenus(container = document) {
  container.querySelectorAll('[data-ecofox-mega-menu]').forEach((details) => {
    if (details.dataset.ecofoxMegaMenuReady === 'true') return;
    details.dataset.ecofoxMegaMenuReady = 'true';
    new EcofoxMegaMenuController(details);
  });

  container.querySelectorAll('.ecofox-mega-menu-accordion').forEach((details) => {
    if (details.dataset.ecofoxAccordionReady === 'true') return;
    const summary = details.querySelector('summary');
    if (!summary) return;

    details.dataset.ecofoxAccordionReady = 'true';
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    summary.addEventListener('click', (event) => event.stopImmediatePropagation(), true);
    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });
  });
}

initEcofoxMegaMenus();

document.addEventListener('shopify:section:load', (event) => initEcofoxMegaMenus(event.target));